import React, { useCallback, useEffect, useRef } from 'react';

import ResizeObserver from 'resize-observer-polyfill';

type PixelAnimation = 'appear' | 'disappear';
type PixelVariant = 'default' | 'blue' | 'yellow' | 'pink';

type PixelCardProps = {
  variant?: PixelVariant;
  gap?: number;
  speed?: number;
  colors?: string;
  noFocus?: boolean;
  autoPlay?: boolean;
  mode?: 'card' | 'overlay';
  className?: string;
  children?: React.ReactNode;
};

export type OptimizeRoutePixelCardProps = Record<string, never>;

class Pixel {
  private readonly width: number;
  private readonly height: number;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly maxSizeInteger = 5;
  private readonly minSize = 1.6;
  private readonly speed: number;
  private readonly sizeStep: number;
  private readonly delay: number;
  private readonly counterStep: number;

  private size = 0;
  private maxSize: number;
  private counter = 0;
  private isIdle = false;
  private isReverse = false;
  private isShimmer = false;

  constructor(
    canvas: HTMLCanvasElement,
    context: CanvasRenderingContext2D,
    private readonly x: number,
    private readonly y: number,
    private readonly color: string,
    speed: number,
    delay: number,
  ) {
    this.width = canvas.width;
    this.height = canvas.height;
    this.ctx = context;
    this.delay = delay;
    this.speed = this.randomBetween(0.1, 0.9) * speed;
    this.sizeStep = Math.random() * 0.4;
    this.maxSize = this.randomBetween(this.minSize, this.maxSizeInteger);
    this.counterStep = Math.random() * 4 + (this.width + this.height) * 0.01;
  }

  get idle(): boolean {
    return this.isIdle;
  }

  private randomBetween(min: number, max: number): number {
    return Math.random() * (max - min) + min;
  }

  private draw(): void {
    const centerOffset = this.maxSizeInteger * 0.5 - this.size * 0.5;
    this.ctx.fillStyle = this.color;
    this.ctx.fillRect(this.x + centerOffset, this.y + centerOffset, this.size, this.size);
  }

  appear(): void {
    this.isIdle = false;
    if (this.counter <= this.delay) {
      this.counter += this.counterStep;
      return;
    }
    if (this.size >= this.maxSize) {
      this.isShimmer = true;
    }
    if (this.isShimmer) {
      this.shimmer();
    } else {
      this.size += this.sizeStep;
    }
    this.draw();
  }

  disappear(): void {
    this.isShimmer = false;
    this.counter = 0;
    if (this.size <= 0) {
      this.isIdle = true;
      return;
    }
    this.size -= 0.1;
    this.draw();
  }

  private shimmer(): void {
    if (this.size >= this.maxSize) {
      this.isReverse = true;
    } else if (this.size <= this.minSize) {
      this.isReverse = false;
    }

    if (this.isReverse) {
      this.size -= this.speed;
    } else {
      this.size += this.speed;
    }
  }
}

const VARIANTS: Record<
  PixelVariant,
  { activeColor: string | null; gap: number; speed: number; colors: string; noFocus: boolean }
> = {
  default: {
    activeColor: null,
    gap: 5,
    speed: 35,
    colors: '#f8fafc,#f1f5f9,#cbd5e1',
    noFocus: false,
  },
  blue: {
    activeColor: '#e0f2fe',
    gap: 10,
    speed: 25,
    colors: '#e0f2fe,#7dd3fc,#0ea5e9',
    noFocus: false,
  },
  yellow: {
    activeColor: '#fef08a',
    gap: 3,
    speed: 20,
    colors: '#fef08a,#fde047,#eab308',
    noFocus: false,
  },
  pink: {
    activeColor: '#fecdd3',
    gap: 6,
    speed: 80,
    colors: '#fecdd3,#fda4af,#e11d48',
    noFocus: true,
  },
};

const clampSpeed = (value: number | undefined, reducedMotion: boolean): number => {
  const min = 0;
  const max = 100;
  const throttle = 0.001;
  const parsed = Number.parseInt(String(value ?? 0), 10);

  if (parsed <= min || reducedMotion) {
    return min;
  }
  if (parsed >= max) {
    return max * throttle;
  }
  return parsed * throttle;
};

const PixelCard = ({
  variant = 'default',
  gap,
  speed,
  colors,
  noFocus,
  autoPlay = false,
  mode = 'card',
  className = '',
  children,
}: PixelCardProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pixelsRef = useRef<Pixel[]>([]);
  const animationRef = useRef<number | null>(null);
  const timePreviousRef = useRef<number>(performance.now());
  const reducedMotion = useRef<boolean>(
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  ).current;

  const variantCfg = VARIANTS[variant] ?? VARIANTS.default;
  const finalGap = gap ?? variantCfg.gap;
  const finalSpeed = speed ?? variantCfg.speed;
  const finalColors = colors ?? variantCfg.colors;
  const finalNoFocus = noFocus ?? variantCfg.noFocus;
  const isOverlay = mode === 'overlay';
  const baseClass = isOverlay ? 'pixel-overlay' : 'pixel-card';
  const finalClassName = className ? `${baseClass} ${className}` : baseClass;

  const initPixels = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (container === null || canvas === null) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const width = Math.floor(rect.width);
    const height = Math.floor(rect.height);
    const ctx = canvas.getContext('2d');

    if (ctx === null) {
      return;
    }

    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const colorsArray = finalColors.split(',');
    const pxs: Pixel[] = [];
    const gapValue = Number.parseInt(String(finalGap), 10);
    // Calculate max pixels based on screen area to ensure visibility
    const estimatedPixels = Math.ceil(width / gapValue) * Math.ceil(height / gapValue);
    const maxPixels = Math.min(estimatedPixels, 1200); // Cap at 1200 for performance

    for (let x = 0; x < width && pxs.length < maxPixels; x += gapValue) {
      for (let y = 0; y < height && pxs.length < maxPixels; y += gapValue) {
        const color = colorsArray[Math.floor(Math.random() * colorsArray.length)];

        const dx = x - width / 2;
        const dy = y - height / 2;
        const distance = Math.hypot(dx, dy);
        const maxDistance = Math.max(Math.hypot(width / 2, height / 2), 1);
        const delay = reducedMotion ? 0 : (distance / maxDistance) * 900 + Math.random() * 80;

        pxs.push(new Pixel(canvas, ctx, x, y, color, clampSpeed(finalSpeed, reducedMotion), delay));
      }
    }
    pixelsRef.current = pxs;
  }, [finalColors, finalGap, finalSpeed, reducedMotion]);

  const doAnimate = useCallback((fnName: PixelAnimation) => {
    const timeNow = performance.now();
    const timePassed = timeNow - timePreviousRef.current;
    const timeInterval = 1000 / 60;

    if (timePassed < timeInterval) {
      animationRef.current = requestAnimationFrame(() => doAnimate(fnName));
      return;
    }
    timePreviousRef.current = timeNow - (timePassed % timeInterval);

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !canvasRef.current) {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    let allIdle = true;
    for (const pixel of pixelsRef.current) {
      pixel[fnName]();
      if (!pixel.idle) {
        allIdle = false;
      }
    }

    if (allIdle) {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    } else {
      animationRef.current = requestAnimationFrame(() => doAnimate(fnName));
    }
  }, []);

  const handleAnimation = useCallback(
    (name: PixelAnimation) => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(() => doAnimate(name));
    },
    [doAnimate],
  );

  const onMouseEnter = (): void => handleAnimation('appear');
  const onMouseLeave = (): void => handleAnimation('disappear');
  const onFocus = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    handleAnimation('appear');
  };
  const onBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    handleAnimation('disappear');
  };

  useEffect(() => {
    // Cancel any existing animation before reinitializing
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    initPixels();
    const observer = new ResizeObserver(() => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      initPixels();
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    if (autoPlay) {
      handleAnimation('appear');
    }
    return () => {
      observer.disconnect();
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Clear pixels array to free memory
      pixelsRef.current = [];
    };
  }, [autoPlay, finalGap, finalSpeed, finalColors, finalNoFocus, handleAnimation, initPixels]);

  return (
    <div
      ref={containerRef}
      className={finalClassName}
      onMouseEnter={isOverlay ? undefined : onMouseEnter}
      onMouseLeave={isOverlay ? undefined : onMouseLeave}
      onFocus={isOverlay || finalNoFocus ? undefined : onFocus}
      onBlur={isOverlay || finalNoFocus ? undefined : onBlur}
      tabIndex={isOverlay || finalNoFocus ? -1 : 0}
    >
      <canvas className="pixel-canvas" ref={canvasRef} />
      {children}
    </div>
  );
};

export const OptimizeRoutePixelCard: React.FC<OptimizeRoutePixelCardProps> = () => (
  <PixelCard
    variant="blue"
    gap={13}
    speed={100}
    colors="#7fd3ff,#4ba6ff,#2f81d5,#1c4f7d"
    autoPlay
    mode="overlay"
    className="optimize-pixel-overlay"
  />
);
